import React from 'react';
import { BuildingDTO } from 'shared/types/building';
import { useDueDiligenceCategories } from 'hooks/useDueDiligence';
import { DueDiligenceScope } from 'shared/types/due-diligence';
import { Skeleton } from '@/components/ui/skeleton';
import DueDiligenceEntityCard from './DueDiligenceEntityCard';

interface DueDiligenceTabProps {
  building: BuildingDTO;
  refreshBuilding: () => void;
}

/**
 * DueDiligenceTab - Displays due diligence categories for a building and its units.
 * Categories, questions, and answer options are defined in settings.
 * Clicking on a category opens the detail page in a new tab.
 */
const DueDiligenceTab: React.FC<DueDiligenceTabProps> = ({
  building,
}) => {
  const { categories, isLoading: isLoadingCategories, error: categoriesError } = useDueDiligenceCategories();

  // Sort units by ID
  const sortedUnits = React.useMemo(() => {
    if (!Array.isArray(building?.units) || building.units.length === 0) {
      return [];
    }
    return [...building.units].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  }, [building?.units]);

  // Check if we have any categories configured
  const buildingCategories = categories.filter(c => c.scope === DueDiligenceScope.BUILDING);
  const unitCategories = categories.filter(c => c.scope === DueDiligenceScope.UNIT);
  const hasCategories = buildingCategories.length > 0 || unitCategories.length > 0;

  if (!building || !building.id) {
    return (
      <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
        <p className="text-neutral-secondary-mid">
          Geen gegevens beschikbaar
        </p>
      </div>
    );
  }

  if (isLoadingCategories) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="row-span-2">
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 text-4xl text-destructive">⚠️</div>
        <h3 className="text-lg font-semibold text-foreground">Fout bij laden</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          {categoriesError}
        </p>
        <p className="mt-4 text-xs text-muted-foreground">
          Controleer of de database migratie is uitgevoerd.
        </p>
      </div>
    );
  }

  if (!hasCategories) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 text-4xl text-muted-foreground">📋</div>
        <h3 className="text-lg font-semibold text-foreground">Geen Due Diligence categorieën</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Er zijn nog geen due diligence categorieën geconfigureerd.
          Ga naar Instellingen → Due Diligence Categorieën om categorieën en vragen toe te voegen.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Building Card - spans 2 rows if there are building categories */}
        {buildingCategories.length > 0 && (
          <div className="row-span-2">
            <DueDiligenceEntityCard
              buildingId={building.id}
              title={`${building.id} - Algemene delen`}
              scope={DueDiligenceScope.BUILDING}
            />
          </div>
        )}

        {/* Unit Cards - each card opens detail page in new tab when category clicked */}
        {unitCategories.length > 0 && sortedUnits.map((unit: any) => (
          <DueDiligenceEntityCard
            key={unit.id}
            buildingId={building.id ?? undefined}
            unitId={unit.id}
            title={`${unit.id}`}
            scope={DueDiligenceScope.UNIT}
          />
        ))}
      </section>
    </div>
  );
};

export default DueDiligenceTab;
